// FE-SCRM-060 — <RowActionsMenu> kebab dropdown.
//
// Example:
//
//     <RowActionsMenu
//       items={[
//         { label: "Edit", onClick: () => setEditing(row) },
//         { label: "Archive", onClick: () => archive(row.id) },
//         { label: "Delete", onClick: () => confirm(row.id), destructive: true },
//       ]}
//     />
//
// Standardizes Edit / Archive / Delete on every list row. Closes on
// outside click, on Escape, and after an item is selected.

import { useEffect, useRef, useState } from "react";


export interface RowAction {
  label: string;
  onClick: () => void;
  destructive?: boolean;
  disabled?: boolean;
}


export interface RowActionsMenuProps {
  items: readonly RowAction[];
  /** ARIA label for the trigger button. Defaults to "Row actions". */
  triggerLabel?: string;
  className?: string;
}


export function RowActionsMenu({
  items, triggerLabel = "Row actions", className,
}: RowActionsMenuProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div
      ref={ref}
      className={"relative inline-block " + (className ?? "")}
    >
      <button
        type="button"
        aria-label={triggerLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={
          "inline-flex h-7 w-7 items-center justify-center rounded "
          + "text-gray-500 hover:bg-gray-100 hover:text-gray-700"
        }
      >
        ⋯
      </button>
      {open && (
        <div
          role="menu"
          className={
            "absolute right-0 z-10 mt-1 min-w-[10rem] rounded-md "
            + "border border-gray-200 bg-white py-1 shadow-lg"
          }
        >
          {items.map((item, idx) => (
            <button
              key={`${item.label}-${idx}`}
              type="button"
              role="menuitem"
              disabled={item.disabled}
              onClick={() => {
                if (item.disabled) return;
                setOpen(false);
                item.onClick();
              }}
              className={
                "block w-full px-3 py-1.5 text-left text-sm "
                + "hover:bg-gray-50 disabled:cursor-not-allowed "
                + "disabled:text-gray-400 "
                + (item.destructive
                  ? "text-rose-600 hover:bg-rose-50 hover:text-rose-700 "
                  : "text-gray-700 ")
              }
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
